import React, { useCallback, useMemo } from "react";

import { faMobile } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { v4 } from "uuid";

import { openDispatchingModal } from "actions";

import AgGridTable from "components/AgGrid/AgGridContainer";
import TableWrapper from "components/AgGrid/TableWrapper";
import { Column } from "components/Layout";
import { ruleColumnDefs } from "components/RuleBookTable/columns";

import { AgGridTables } from "constants/aggrid";
import { ModalTypes } from "constants/navigation";

import { ValueSource } from "containers/Settings/RuleBooks/constants";
import { getGstFee } from "containers/Settings/RuleBooks/Rules/CustomRules/ValueClause";

import { openModalLink } from "lib/navigation";

import { selectRulesAggridDataByRuleBookIdLookup } from "selectors/aggrid/rules";

const getRowId = params => params.data.rule.id;

export const RuleBookTable = ({ ruleBookId }) => {
  const rowData = useSelector(selectRulesAggridDataByRuleBookIdLookup)[
    ruleBookId
  ];

  const dispatch = useDispatch();

  const onClickAddCustomRule = useCallback(() => {
    dispatch(
      openDispatchingModal(ModalTypes.EditRule, window.location.hash, {
        rule: {
          criteria: [],
          category: "",
          comment: "",
          gl_code: "",
          billing_tags: [],
          from_business: { source: "", attribute: "", fieldId: "", index: "" },
          id: v4(),
          input_field: { attribute: "", fieldId: "" },
          price_multiplier: 1,
          name: "",
          to_business: { source: "", attribute: "", fieldId: "", index: "" },
          value: {
            amount: { source: ValueSource.CONSTANT, value: 0 },
            offset: { source: ValueSource.CONSTANT, value: 0 },
            rounding_value: 0,
            unit_offset: { source: ValueSource.CONSTANT, value: 0 },
            unit_limit: { source: ValueSource.CONSTANT, value: 0 },
            units: { source: "", attribute: "", fieldId: "", index: "" },
            fees: [getGstFee("exclusive")],
          },
          include_if_zero: false,
          rule_book_id: ruleBookId,
        },
      }),
    );
  }, [dispatch, ruleBookId]);

  const addManagedRule = useCallback(
    () =>
      dispatch(
        openDispatchingModal(
          ModalTypes.RuleLibrary,
          window.location.hash,
          { ruleBookId },
          { ruleBookId },
        ),
      ),
    [dispatch, ruleBookId],
  );

  const handleOpenRuleTester = useCallback(() => {
    // Stash the rule data in state - it's (probably) too big for the URL method.

    openModalLink(
      ModalTypes.RuleTester,
      [[], ruleBookId, false],
      window.location.hash,
    );
  }, [ruleBookId]);

  const additionalActions = useMemo(
    () => [
      {
        title: "Add Managed Rule",
        icon: faMobile,
        onClick: addManagedRule,
        dataTour: "addManagedRule",
      },
      {
        title: "Add Custom Rule",
        icon: faMobile,
        onClick: onClickAddCustomRule,
        dataTour: "addCustomRule",
      },
      {
        title: "Test Rules",
        icon: faMobile,
        onClick: handleOpenRuleTester,
        dataTour: "addCustomRule",
      },
    ],
    [addManagedRule, handleOpenRuleTester, onClickAddCustomRule],
  );

  return (
    <Column fullHeight>
      <AgGridTable
        additionalActions={additionalActions}
        columnDefs={ruleColumnDefs}
        getRowId={getRowId}
        rowData={rowData}
        showGlobalSearchFilter={false}
        WrapperComponent={TableWrapper}
        tableName={AgGridTables.RULE_BOOK}
        pagination={false}
        enableBrowserTooltips
      />
    </Column>
  );
};
